import axios from "axios";
import store from "@/store";
import { message } from "ant-design-vue";
import { geoBlockedNotice, mfaCheckUnavailableNotice, retryAfterStepUp, withMfaHeader } from "@/utils/mfa";
import { requestStepUp } from "@/utils/stepUp";
import { reportGeoBlock } from "@/utils/geoBlockReport";
import { withClientHeaders } from "@/utils/clientHeaders";
import { handleStandardError } from "@/utils/standardErrorHandler";

// crm-api 전용 인스턴스 (certilife 관리자 JWT를 crm-api 가 그대로 검증한다)
const crmApiInstance = axios.create({
  baseURL: process.env.VUE_APP_CRM_API_URL,
});

crmApiInstance.interceptors.request.use(
  (config) => {
    // 글로벌 로딩은 config에서 명시적으로 요청한 경우에만 활성화
    if (config.showGlobalLoading !== false) {
      store.commit("setIsLoading", true);
    }
    withClientHeaders(config);

    if (localStorage.getItem("accessTokenCerti")) {
      config.headers.Authorization = `Bearer ${localStorage.getItem("accessTokenCerti")}`;
      withMfaHeader(config);
    }
    return config;
  },
  (error) => {
    store.commit("setIsLoading", false);
    return Promise.reject(error);
  }
);

let isTokenRefreshing = false;
let failedRequestsQueue = [];

crmApiInstance.interceptors.response.use(
  (response) => {
    if (response.config.showGlobalLoading !== false) {
      store.commit("setIsLoading", false);
    }
    return response;
  },
  async (error) => {
    if (error.config && error.config.showGlobalLoading !== false) {
      store.commit("setIsLoading", false);
    }
    const originalRequest = error.config;

    // 국외 IP 의 모든 요청(403 GEO_BLOCKED): 안내 문구를 한 번(같은 key 라 겹치지 않음) 띄우고 그대로 실패시킨다(step-up 없음, AC-GEO-010c)
    const geoNotice = geoBlockedNotice(error);
    if (geoNotice) {
      reportGeoBlock("REQUEST", originalRequest);
      message.warning({ content: geoNotice, key: "GEO_BLOCKED" });
      return Promise.reject(error);
    }

    // 2FA 필요 여부 확인 실패(503 MFA_CHECK_UNAVAILABLE): 고정 문구 한 번(같은 key) 뒤 실패 — 재전송·step-up 없음(F-9)
    const unavailable = mfaCheckUnavailableNotice(error);
    if (unavailable) {
      message.error({ content: unavailable, key: "MFA_CHECK_UNAVAILABLE" });
      return Promise.reject(error);
    }

    // 2차 인증 필요(MFA_REQUIRED·SEND_AUTH_REQUIRED) → step-up 창 → 통과하면 원래 요청 한 번 재전송
    const stepUp = retryAfterStepUp(error, { requestStepUp, resend: crmApiInstance });
    if (stepUp) return stepUp;

    // 새 계약 오류 코드(ACCOUNT_INACTIVE·PERMISSION_DENIED·AUDIT_UNAVAILABLE·OPERATOR_ACTING_DENIED 등): 알림 한 번 뒤 실패 — 토큰 갱신하지 않음(W6 E2)
    if (await handleStandardError(error, { notify: originalRequest?.showErrorNotice !== false })) return Promise.reject(error);

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (!isTokenRefreshing) {
        isTokenRefreshing = true;
        await store.dispatch("requestAccessToken");
        isTokenRefreshing = false;

        failedRequestsQueue.forEach((queueItem) => {
          queueItem.resolve(crmApiInstance(queueItem.originalRequest));
        });
        failedRequestsQueue = [];

        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem("accessTokenCerti")}`;
        return crmApiInstance(originalRequest);
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve,
            reject,
            originalRequest,
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

export default crmApiInstance;
